import React, { useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Globe, Check } from 'lucide-react';

// Region → label key. Heading text routes through i18n so the
// section titles translate alongside the language list itself.
const REGION_ORDER = ['global', 'european', 'middle_eastern', 'asian', 'african'];
const REGION_LABEL_KEYS = {
  global:          'settings.regions.global',
  european:        'settings.regions.european',
  middle_eastern:  'settings.regions.middleEastern',
  asian:           'settings.regions.asian',
  african:         'settings.regions.african',
};
const REGION_FALLBACKS = {
  global:          'Global',
  european:        'European Languages',
  middle_eastern:  'Middle Eastern Languages',
  asian:           'Asian Languages',
  african:         'African Languages',
};

const LanguageSelector = () => {
  const { currentLanguage, changeLanguage, languages, t } = useLanguage();

  // Group languages by region while preserving the source-order
  // inside each group (avoids surprising re-sorts on rerenders).
  const grouped = useMemo(() => {
    const out = Object.fromEntries(REGION_ORDER.map((r) => [r, []]));
    for (const lang of languages) {
      const r = out[lang.region] ? lang.region : 'global';
      out[r].push(lang);
    }
    return out;
  }, [languages]);

  return (
    <Card className="border-2 border-healthloop-mint-200" data-testid="language-selector-card">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Globe className="h-6 w-6 mr-2 text-healthloop-mint-600" />
          {t('profile.language')}
        </CardTitle>
        <CardDescription>
          {t('profile.selectLanguage')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-5" data-testid="language-selector-groups">
          {REGION_ORDER.map((region) => {
            const items = grouped[region];
            if (!items || items.length === 0) return null;
            const label = t(REGION_LABEL_KEYS[region], REGION_FALLBACKS[region]);
            return (
              <div key={region} data-testid={`language-region-${region}`}>
                <p className="text-[11px] font-semibold tracking-wider uppercase text-healthloop-mint-700 mb-2">
                  {label}
                </p>
                <div className="grid gap-2">
                  {items.map((lang) => {
                    const active = currentLanguage === lang.code;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => changeLanguage(lang.code)}
                        className={`flex items-center justify-between gap-3 p-3 rounded-lg border-2 transition-all hover:border-healthloop-mint-400 ${
                          active
                            ? 'border-healthloop-mint-500 bg-healthloop-mint-50'
                            : 'border-gray-200 bg-white'
                        }`}
                        aria-pressed={active}
                        data-testid={`language-option-${lang.code}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-2xl shrink-0" aria-hidden="true">{lang.flag}</span>
                          <div className="text-left min-w-0">
                            <p className="font-semibold text-gray-900 truncate">{lang.name}</p>
                            <p className="text-[11px] text-gray-500 uppercase tracking-wide">{lang.code}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {lang.starter && (
                            <span
                              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                              title={t('settings.starterBadgeTooltip', 'Machine-translated. Native-speaker review pending.')}
                              data-testid={`language-starter-badge-${lang.code}`}
                            >
                              {t('settings.starterBadge', 'Starter')}
                            </span>
                          )}
                          {active && (
                            <Check className="h-5 w-5 text-healthloop-mint-600" aria-hidden="true" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 p-3 bg-healthloop-mint-50 border border-healthloop-mint-200 rounded-lg">
          <p className="text-sm text-healthloop-mint-900">
            <strong>💡 {t('common.note')}:</strong> {t('settings.languageNote')}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LanguageSelector;
